/**
 * Utility functions for handling semester calculations and generation
 */

/**
 * Generate all semesters from a starting year until the current semester
 * @param {number} gradYear - Expected graduation year
 * @returns {Array} Array of semester objects with id, display name, and term info
 */
export const generateSemestersUntilCurrent = (gradYear) => {
  if (!gradYear) return [];
  
  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth() + 1; // 0-based, so add 1
  
  // Determine current semester
  let currentSemester;
  if (currentMonth >= 1 && currentMonth <= 5) {
    currentSemester = 'spring';
  } else if (currentMonth >= 6 && currentMonth <= 8) {
    currentSemester = 'summer';
  } else {
    currentSemester = 'fall';
  }
  
  // Calculate starting year (typically 4 years before grad year)
  const startYear = gradYear - 4;
  const semesters = [];
  
  for (let year = startYear; year <= currentYear; year++) {
    // Fall semester
    const fallYear = year;
    const fallId = `F${fallYear.toString().slice(-2)}`;
    if (year < currentYear || (year === currentYear && ['fall', 'summer'].includes(currentSemester))) {
      semesters.push({
        id: fallId,
        term: fallId,
        displayName: `Fall ${fallYear}`,
        year: fallYear,
        season: 'Fall',
        isCurrent: year === currentYear && currentSemester === 'fall',
        isPast: year < currentYear || (year === currentYear && currentSemester !== 'fall'),
        isUpcoming: false
      });
    }
    
    // Spring semester (next calendar year)
    const springYear = year + 1;
    const springId = `S${springYear.toString().slice(-2)}`;
    if (springYear <= currentYear || (springYear === currentYear + 1 && currentSemester === 'spring')) {
      semesters.push({
        id: springId,
        term: springId,
        displayName: `Spring ${springYear}`,
        year: springYear,
        season: 'Spring',
        isCurrent: springYear === currentYear && currentSemester === 'spring',
        isPast: springYear < currentYear || (springYear === currentYear && ['fall'].includes(currentSemester)),
        isUpcoming: false
      });
    }
  }
  
  // Add just the next upcoming semester for planning
  if (currentSemester === 'spring') {
    // If current is spring, next is fall of same year
    const nextFallId = `F${currentYear.toString().slice(-2)}`;
    semesters.push({
      id: nextFallId,
      term: nextFallId,
      displayName: `Fall ${currentYear}`,
      year: currentYear,
      season: 'Fall',
      isCurrent: false,
      isPast: false,
      isUpcoming: true
    });
  } else {
    // If current is fall/summer, next is spring of next year
    const nextYear = currentYear + 1;
    const nextSpringId = `S${nextYear.toString().slice(-2)}`;
    semesters.push({
      id: nextSpringId,
      term: nextSpringId,
      displayName: `Spring ${nextYear}`,
      year: nextYear,
      season: 'Spring',
      isCurrent: false,
      isPast: false,
      isUpcoming: true
    });
  }
  
  // Sort chronologically (most recent first)
  return semesters.sort((a, b) => {
    if (a.year !== b.year) return b.year - a.year; // More recent years first
    return a.season === 'Fall' ? -1 : 1; // Fall comes after Spring in same academic year, so Fall is more recent
  });
};

/**
 * Get the current semester term ID
 * @returns {string} Current semester term (e.g., "F25", "S26")
 */
export const getCurrentTerm = () => {
  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth() + 1;
  
  if (currentMonth >= 1 && currentMonth <= 5) {
    // Spring semester
    return `S${currentYear.toString().slice(-2)}`;
  } else if (currentMonth >= 6 && currentMonth <= 8) {
    // Summer - treat as fall preparation
    return `F${currentYear.toString().slice(-2)}`;
  } else {
    // Fall semester
    return `F${currentYear.toString().slice(-2)}`;
  }
};

/**
 * Parse a term ID into readable information
 * @param {string} termId - Term ID like "F25" or "S26"
 * @returns {Object} Object with year, season, and display name
 */
export const parseTermId = (termId) => {
  if (!termId || termId.length !== 3) {
    return { year: null, season: null, displayName: 'Unknown Term' };
  }
  
  const season = termId[0];
  const yearSuffix = termId.slice(1);
  const fullYear = 2000 + parseInt(yearSuffix);
  
  const seasonNames = {
    'F': 'Fall',
    'S': 'Spring',
    'W': 'Winter',
    'U': 'Summer'
  };
  
  return {
    year: fullYear,
    season: seasonNames[season] || 'Unknown',
    displayName: `${seasonNames[season] || 'Unknown'} ${fullYear}`
  };
};

/**
 * Check if a semester is in the future
 * @param {string} termId - Term ID like "F25" or "S26"
 * @returns {boolean} True if the semester is in the future
 */
export const isFutureSemester = (termId) => {
  const current = getCurrentTerm();
  const currentInfo = parseTermId(current);
  const termInfo = parseTermId(termId);
  
  if (termInfo.year > currentInfo.year) return true;
  if (termInfo.year < currentInfo.year) return false;
  
  // Same year - check season
  if (currentInfo.season === 'Fall' && termInfo.season === 'Spring') {
    return true;
  }
  
  return false;
};

/**
 * Get all terms that should have course data available
 * @returns {Array} Array of term IDs that courses might be available for
 */
export const getAvailableTerms = () => {
  const currentYear = new Date().getFullYear();
  const terms = [];
  
  // Add past few years and future few years
  for (let offset = -2; offset <= 3; offset++) {
    const year = currentYear + offset;
    const yearSuffix = year.toString().slice(-2);
    terms.push(`F${yearSuffix}`); // Fall
    terms.push(`S${(year + 1).toString().slice(-2)}`); // Spring of next calendar year
  }
  
  return terms;
};